/**
 * Product Detail Page - Clara Vale design.
 *
 * Features:
 * - Inline name editing (pencil icon)
 * - Status toggle (click badge)
 * - Delete with confirmation dialog
 * - Attribute editing via per-card pencil icon (DetailCard pattern)
 * - Image upload/replace via ImageUpload component
 * - Category change dropdown
 * - Registrations / Variants in DetailCards
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { AppShell } from "@/admin/components/app-shell";
import { useParams, useNavigate } from "react-router";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { EntityHistoryCard } from "@/shared/components/workflows";
import {
  useProduct,
  useProductVariants,
  useHoldings,
  useProductBrands,
  useProductCategories,
  useProductFormMetadata,
  useVariantFormMetadata,
  useCreateVariant,
  useUploadFile,
  useTemplateAttributes,
} from "@/admin/hooks/use-products";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Package,
  Pencil,
  Plus,
  Check,
  X,
  Trash2,
  Camera,
  FileText,
  Users,
  ChevronRight,
} from "lucide-react";
import { AccessLevelPanel } from "@/admin/components/access-level-panel";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { useClaims } from "@/admin/hooks/use-claims";
import { DetailCard, FieldRow } from "@/admin/components/shared/detail-card";
import { EditableDetailGridCard, type EditableGridSection } from "@/admin/components/shared/editable-detail-grid-card";
import { defineField, FILE_FIELD_TYPES, useForm } from "@/shared/lib/forms";
import { buildFormFieldFromMeta } from "@/shared/lib/forms/generated-metadata";
import type { FormField, FormFieldMeta, FieldType } from "@/shared/lib/forms";
import type { CardFieldItem } from "@/admin/components/shared/editable-card-types";
import { FilterableDetailList } from "@/admin/components/shared/filterable-detail-list";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  usePagination,
  PaginationControls,
} from "@/admin/components/paginated-table";
import { ImageUpload } from "@/admin/components/image-upload";
import { Button } from "@/shared/components/ui/button";
import { Image } from "@/shared/components/ui/image";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/shared/components/ui/select";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { ConfirmationDialog } from "@/admin/components/shared/confirmation-dialog";
import api from "@/admin/lib/api";
import { useFeatureFlag, FLAGS } from "@/admin/hooks/use-feature-flag";
import {
  useProductAvailability,
  useAddProductAvailability,
  useRemoveProductAvailability,
  useUpdateProductAvailability,
  useGeoZonesAll,
  type GeoZone,
  type ProductAvailabilityRecord,
} from "@/admin/hooks/use-geo";
import { Badge } from "@/shared/components/ui/badge";
import type { FieldMeta, AttributeValue } from "@/admin/types/products";
import { cn } from "@/shared/lib/utils";
import { formatClaimId } from "@/admin/lib/formatting";

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

import { toastApiError } from '@/shared/lib/api-error'
import { ValueStockCard } from '@/admin/components/shared/value-stock-card'
import { readQrConfig, readStockConfig } from '@/admin/lib/value-stock'
import { useFormatting } from '@/admin/hooks/use-formatting'

export function ProductDetailPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: product, isLoading, refetch } = useProduct(uuid ?? null);

  // Name edit state
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Attribute edit state
  const [savingAttrs, setSavingAttrs] = useState(false);

  // Image edit state
  const [editingImage, setEditingImage] = useState(false);

  // Availability state
  const [editingAvail, setEditingAvail] = useState(false);
  const geoEnabled = useFeatureFlag(FLAGS.GEOGRAPHIC_AVAILABILITY);
  const { data: availability } = useProductAvailability(
    geoEnabled ? (uuid ?? null) : null,
  );
  const { data: allPsaData } = useGeoZonesAll(
    geoEnabled ? { zone_type: "plant_service_area" } : undefined,
  );
  const addAvail = useAddProductAvailability();
  const removeAvail = useRemoveProductAvailability();
  const updateAvail = useUpdateProductAvailability();
  const allPsaZones = allPsaData?.results ?? [];
  const claimsParams = useMemo(
    () => (uuid ? { product_uuid: uuid, page_size: "20" } : undefined),
    [uuid],
  );
  const { data: claimsData, isPending: claimsPending, isFetching: claimsFetching } = useClaims(claimsParams);
  const { locale, formatDate } = useFormatting();
  // Fetch form metadata for attribute types
  const templateUuid = product?.product_template?.uuid ?? null;
  const { data: fieldMeta } = useProductFormMetadata(templateUuid);
  const { data: templateAttrs } = useTemplateAttributes(templateUuid);
  // Variant-level schema lives on the parent. Needed so a field added
  // on the template after this variant was created still shows empty
  // and can be filled, same as product-level fields already do.
  const { data: variantFieldMeta } = useVariantFormMetadata(
    product?.parent_product_uuid ?? null,
  );
  const { data: categories } = useProductCategories();
  const brandsEnabled = useFeatureFlag(FLAGS.BRANDS);
  const { data: brands } = useProductBrands();

  // Flatten category tree for select dropdown
  const flatCategories = useMemo(() => {
    const result: { uuid: string; name: string }[] = [];
    function walk(cats: typeof categories, prefix = "") {
      for (const cat of cats ?? []) {
        const label = prefix ? `${prefix} > ${cat.name}` : cat.name;
        result.push({ uuid: cat.uuid, name: label });
        if (cat.children?.length) walk(cat.children, label);
      }
    }
    walk(categories);
    return result;
  }, [categories]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const startEdit = useCallback(() => {
    if (!product) return;
    setEditName(product.cached_short_name || product.cached_long_name || "");
    setEditing(true);
  }, [product]);

  async function saveEdit() {
    if (!uuid || !editName.trim()) return;
    setSaving(true);
    try {
      await api.put(`/products/items/${uuid}/`, {
        attribute_values: { name: editName.trim() },
      });
      toast.success("Product updated");
      setEditing(false);
      refetch();
    } catch (err) {
      toastApiError(err, "Failed to update product");
    } finally {
      setSaving(false);
    }
  }

  async function toggleStatus() {
    if (!product || !uuid) return;
    const newStatus = product.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED";
    try {
      await api.put(`/products/items/${uuid}/`, { status: newStatus });
      toast.success(
        `Product ${newStatus === "PUBLISHED" ? "published" : "unpublished"}`,
      );
      refetch();
    } catch (err) {
      toastApiError(err, "Failed to update status");
    }
  }

  async function handleDelete() {
    if (!uuid) return;
    setDeleting(true);
    try {
      await api.delete(`/products/items/${uuid}/`);
      toast.success("Product deleted");
      setDeleteOpen(false);
      navigate("/manage/products");
    } catch (err) {
      toastApiError(err, "Failed to delete product");
    } finally {
      setDeleting(false);
    }
  }

  async function saveAttrs(values: Record<string, unknown>) {
    if (!uuid) return;
    setSavingAttrs(true);
    try {
      // The synthetic brand field (code_name="__brand_fk__") rides on the
      // same form as the EAV attribute_values but maps to the Product.brand
      // FK on the backend. Strip it out before sending attribute_values
      // so it doesn't accidentally land in the EAV table, and pipe it
      // through brand_uuid instead.
      const { __brand_fk__: brandValue, ...attributeValues } = values;
      const payload: Record<string, unknown> = {
        attribute_values: attributeValues,
      };
      if (brandsEnabled && "__brand_fk__" in values) {
        payload.brand_uuid =
          typeof brandValue === "string" && brandValue ? brandValue : null;
      }
      await api.put(`/products/items/${uuid}/`, payload);
      toast.success("Attributes updated");
      refetch();
    } catch (err) {
      toastApiError(err, "Failed to update attributes");
      throw err;
    } finally {
      setSavingAttrs(false);
    }
  }

  async function handleCategoryChange(categoryUuid: string | null) {
    if (!uuid) return;
    try {
      await api.put(`/products/items/${uuid}/`, {
        category_uuid: categoryUuid,
      });
      toast.success("Category updated");
      refetch();
    } catch (err) {
      toastApiError(err, "Failed to update category");
    }
  }

  async function handleBrandChange(brandUuid: string | null) {
    if (!uuid) return;
    try {
      await api.put(`/products/items/${uuid}/`, { brand_uuid: brandUuid });
      toast.success("Brand updated");
      refetch();
    } catch (err) {
      toastApiError(err, "Failed to update brand");
    }
  }

  async function handleImageChange(imageUuid: string | null) {
    if (!uuid) return;
    try {
      await api.put(`/products/items/${uuid}/`, { image_uuid: imageUuid });
      toast.success("Image updated");
      setEditingImage(false);
      refetch();
    } catch (err) {
      toastApiError(err, "Failed to update image");
    }
  }

  // ---------------------------------------------------------------------------
  // Loading / Not found
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="text-[13px] text-[var(--ink-3)]">
            {t("common.loading")}
          </p>
        </main>
      </AppShell>
    );
  }

  if (!product) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="text-[13px] text-[var(--ink-3)]">Product not found.</p>
        </main>
      </AppShell>
    );
  }

  const isBase = product.product_type === "VARIANT_BASE";
  const attrs = product.attribute_values ?? [];
  const stockedHoldingAttrs = (templateAttrs ?? []).filter((attr) => {
    if (attr.attribute_level !== "HOLDING") return false;
    const stock = readStockConfig(attr.configuration_data);
    return stock !== null && stock.scope_entity_key === "product";
  });
  const canEditStock =
    can("products.edit.global") || can("products.edit.acl");

  // Build a field type map from metadata
  const fieldTypeMap = new Map<string, FieldMeta>();
  if (fieldMeta) {
    for (const f of fieldMeta) {
      fieldTypeMap.set(f.code_name, f);
    }
  }
  if (variantFieldMeta) {
    for (const f of variantFieldMeta) {
      fieldTypeMap.set(f.code_name, f);
    }
  }

  return (
    <AppShell>
      <main className="page-container py-10">
        {/* Back */}
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="link"
            onClick={() => navigate("/manage/products")}
            className="flex cursor-pointer items-center gap-1 bg-transparent px-0 text-[11px] text-[var(--ink-3)]"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to Products
          </Button>
          {product.parent_product_uuid && (
            <Button
              variant="link"
              onClick={() =>
                navigate(`/manage/products/${product.parent_product_uuid}`)
              }
              className="flex cursor-pointer items-center gap-1 bg-transparent px-0 text-[11px] text-[var(--green)]"
            >
              <ArrowLeft className="h-3 w-3" />
              Parent Product
            </Button>
          )}
        </div>

        {/* Hero - image + info side by side */}
        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-[120px_1fr] items-end">
          {/* Image - clickable to change */}
          {editingImage ? (
            <div>
              <ImageUpload
                value={product.image?.uuid ?? null}
                previewUrl={product.image?.file_url ?? null}
                // Pass through both a replace (new uuid) and a clear (null)
                // so removing the image persists — the backend clears the
                // image FK on a null image_uuid. Previously null was
                // dropped, so images could only be replaced, not deleted.
                onChange={(imageUuid) => handleImageChange(imageUuid)}
                label=""
              />
              <Button
                variant="link"
                onClick={() => setEditingImage(false)}
                className="mt-2 cursor-pointer bg-transparent px-0 text-[10px] text-[var(--ink-3)]"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div
              className="group relative flex h-32 w-32 cursor-pointer items-center justify-center overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]"
              onClick={() => setEditingImage(true)}
            >
              <Image
                src={product.image?.file_url}
                thumbnailSrc={product.image?.thumbnail_url}
                alt={product.cached_short_name}
                className="h-full w-full"
                fallback={<Package className="h-20 w-20 text-[var(--ink-3)] opacity-30" />}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                <Camera className="h-5 w-5 text-white" />
              </div>
            </div>
          )}

          {/* Info */}
          <div>
            {/* Title - editable + action icons */}
            {editing ? (
              <div className="mb-2 flex items-center gap-2">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="clara-title flex-1 border-0 border-b border-[var(--green)] bg-transparent px-0 pb-1 outline-none focus-visible:ring-0"
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={saveEdit}
                  disabled={saving}
                  className="h-7 w-7 cursor-pointer rounded-[4px] bg-[var(--green)] text-[var(--bg)]"
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => setEditing(false)}
                  className="h-7 w-7 cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule)] bg-transparent text-[var(--ink-3)]"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <div className="mb-2 flex items-end gap-2">
                <h1 className="clara-title flex-1">
                  {product.cached_short_name || product.cached_long_name}
                </h1>
                <div className="mt-2 flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={startEdit}
                    className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[4px] bg-transparent text-[var(--ink-3)] transition-colors duration-100 hover:bg-[var(--bg-subtle)]"
                    title="Edit name"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setDeleteOpen(true)}
                    className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[4px] bg-transparent text-[var(--ink-3)] transition-colors duration-100 hover:bg-[var(--status-error-bg-subtle)] hover:text-[#dc2626]"
                    title="Delete product"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <AccessLevelPanel
                    resourceType="product"
                    objectId={product.id ?? null}
                  />
                </div>
              </div>
            )}

            {/* Meta row */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Button
                variant="ghost"
                onClick={toggleStatus}
                className={cn(
                  "cursor-pointer rounded-[3px] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.08em] transition-colors duration-150",
                  product.status === "PUBLISHED"
                    ? "bg-[var(--green-bg-muted)] text-[var(--green)]"
                    : "bg-[var(--bg-subtle)] text-[var(--ink-3)]",
                )}
                title={
                  product.status === "PUBLISHED"
                    ? "Click to unpublish"
                    : "Click to publish"
                }
              >
                {product.status.toLowerCase()}
              </Button>

              {product.product_template?.parent_template && (
                <Badge variant="outline" className="text-[10px]">
                  {product.product_template.parent_template.name}
                </Badge>
              )}

              <Select
                value={product.category?.uuid ?? ""}
                onValueChange={(val) => handleCategoryChange(val || null)}
              >
                <SelectTrigger className="flex cursor-pointer items-center gap-0.5 border-0 bg-transparent text-[11px] text-[var(--ink-3)] transition-colors duration-100">
                  <span className="truncate">
                    {flatCategories.find(
                      (c) => c.uuid === product.category?.uuid,
                    )?.name ??
                      product.category?.name ??
                      "No category"}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {flatCategories.map((cat) => (
                    <SelectItem key={cat.uuid} value={cat.uuid}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {brandsEnabled && (
                <Select
                  value={product.brand?.uuid ?? ""}
                  onValueChange={(val) => handleBrandChange(val || null)}
                >
                  <SelectTrigger className="flex cursor-pointer items-center gap-0.5 border-0 bg-transparent text-[11px] text-[var(--ink-3)] transition-colors duration-100">
                    <span className="truncate">
                      {brands?.find((b) => b.uuid === product.brand?.uuid)?.name ??
                        product.brand?.name ??
                        "No brand"}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {(brands ?? []).map((brand) => (
                      <SelectItem key={brand.uuid} value={brand.uuid}>
                        {brand.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <span className="text-[11px] text-[var(--ink-3)]">
                {formatDate(product.created_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Card grid - consistent with claims/registration detail */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Attributes card (+ parent attributes when variant) */}
          <AttributesSectionCard
            className="md:col-span-2"
            attrs={attrs}
            savingAttrs={savingAttrs}
            fieldTypeMap={fieldTypeMap}
            saveAttrs={saveAttrs}
            parentProductUuid={product.parent_product_uuid}
            productData={product.product_data}
            fieldMeta={fieldMeta}
            variantFieldMeta={variantFieldMeta}
            brandsEnabled={brandsEnabled}
            currentBrandUuid={product.brand?.uuid ?? null}
            availableBrands={brands ?? []}
          />

          {/* Availability card (feature-flagged) */}
          {geoEnabled && !isBase && (
            <AvailabilityCard
              availability={availability ?? []}
              allPsaZones={allPsaZones}
              editingAvail={editingAvail}
              setEditingAvail={setEditingAvail}
              productUuid={uuid!}
              addAvail={addAvail}
              removeAvail={removeAvail}
              updateAvail={updateAvail}
            />
          )}
          {geoEnabled && isBase && (
            <DetailCard
              title="Plant Service Areas"
              icon={<MapPin className="h-3.5 w-3.5" />}
              collapsible={false}
            >
              <div className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
                Availability is managed at the variant level. Select a variant
                to view or edit its geographic availability.
              </div>
            </DetailCard>
          )}

          {!isBase && stockedHoldingAttrs.length > 0 && (
            // Pair the cards only when there are two or more; a lone stocked
            // attribute (the common case) takes the full row rather than
            // leaving an empty column beside it.
            <div
              className={`md:col-span-2 grid grid-cols-1 gap-4 ${
                stockedHoldingAttrs.length > 1 ? 'md:grid-cols-2' : ''
              }`}
            >
              {stockedHoldingAttrs.map((attr) => (
                <ValueStockCard
                  key={attr.uuid}
                  attribute={attr.code_name}
                  label={attr.display_name}
                  scopeEntity="product"
                  scopeId={product.uuid}
                  canEdit={canEditStock}
                  qrEnabled={Boolean(readQrConfig(attr.configuration_data))}
                />
              ))}
            </div>
          )}

          {/* Variants / Registrations card - the most relevant child
              collection for the product. */}
          {!isBase && (
            <DetailCard
              title="Registrations"
              icon={<Users className="h-3.5 w-3.5" />}
              collapsible={false}
            >
              <RegistrationsContent productUuid={product.uuid} />
            </DetailCard>
          )}
          {isBase && <VariantsCard productUuid={product.uuid} />}

          {/* Claims filed against registrations of this product */}
          <DetailCard
            title="Claims"
            icon={<FileText className="h-3.5 w-3.5" />}
            collapsible={false}
            trailing={
              claimsData && claimsData.count > 20 ? (
                <button
                  onClick={() => navigate(`/claims?product_uuid=${product.uuid}`)}
                  className="inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--green)] transition-opacity duration-150 hover:opacity-70"
                >
                  View all
                  <ChevronRight className="h-3 w-3" />
                </button>
              ) : undefined
            }
          >
            {claimsPending || (claimsFetching && (!claimsData || claimsData.results.length === 0)) ? (
              <p className="py-4 text-center text-[12px] text-[var(--ink-3)]">
                {t("common.loading")}
              </p>
            ) : !claimsData || claimsData.results.length === 0 ? (
              <p className="py-4 text-center text-[12px] text-[var(--ink-3)]">
                No claims for this product
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {claimsData.results.map((claim) => (
                  <button
                    key={claim.uuid}
                    onClick={() => navigate(`/claims/${claim.uuid}`)}
                    className="flex w-full cursor-pointer items-center justify-between rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3.5 py-3 text-left transition-colors hover:border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
                  >
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="truncate text-[13px] font-normal text-[var(--ink)]">
                        {claim.claim_schema_name}
                      </span>
                      <span className="text-[11px] text-[var(--ink-3)]">
                        <span className="font-mono tracking-[0.02em]">
                          {formatClaimId(claim.uuid)}
                        </span>
                        {" · "}
                        {new Date(claim.created_at).toLocaleDateString(
                          locale,
                          {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                        {claim.assigned_to && (
                          <> · {claim.assigned_to.name}</>
                        )}
                      </span>
                    </div>
                    <StatusBadge
                      status={claim.claim_state || "Open"}
                      color={claim.claim_state_color}
                      className="ml-3 shrink-0"
                    />
                  </button>
                ))}
              </div>
            )}
          </DetailCard>

          {can("products.view_change_log") && (
            <EntityHistoryCard
              entityKey="product"
              entityId={uuid ?? null}
              title="Change history"
              className="md:col-span-2"
            />
          )}

        </div>

        <ConfirmationDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete product?"
          description={
            <>
              Delete{" "}
              <strong>
                {product.cached_short_name ||
                  product.cached_long_name ||
                  "this product"}
              </strong>
              ? This action cannot be undone.
            </>
          }
          confirmLabel="Delete"
          pendingLabel="Deleting..."
          destructive
          onConfirm={handleDelete}
          isPending={deleting}
        />
      </main>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Combined Attributes Section Card
// ---------------------------------------------------------------------------

function AttributesSectionCard({
  attrs,
  savingAttrs,
  fieldTypeMap,
  saveAttrs,
  parentProductUuid,
  productData,
  fieldMeta,
  variantFieldMeta,
  brandsEnabled,
  currentBrandUuid,
  availableBrands,
  className,
}: {
  attrs: AttributeValue[];
  savingAttrs: boolean;
  fieldTypeMap: Map<string, FieldMeta>;
  saveAttrs: (values: Record<string, unknown>) => Promise<void>;
  parentProductUuid?: string | null;
  productData: Record<string, unknown>;
  fieldMeta: FieldMeta[] | undefined;
  variantFieldMeta?: FieldMeta[];
  brandsEnabled?: boolean;
  currentBrandUuid?: string | null;
  availableBrands?: { uuid: string; name: string }[];
  className?: string;
}) {
  // Upload handler for DOCUMENT/FILE/IMAGE attributes edited in the dialog.
  // Without it the generated FileField picks a file but never uploads it,
  // so the attribute saves empty ({}). Mirrors the claim action pattern.
  const uploadFile = useUploadFile();
  const handleFileUpload = useCallback(
    async (file: File, field: FormFieldMeta) => {
      const uploaded = await uploadFile.mutateAsync({
        file,
        use_case: field.file_use_case || "PRODUCT_DOCUMENT",
      });
      return {
        uuid: uploaded.uuid,
        file_name: uploaded.file_name,
        file_url: uploaded.file_url,
      };
    },
    [uploadFile],
  );

  // Split attributes by level.
  const variantAttrs = attrs.filter(
    (a) =>
      a.code_name !== "product_ranking" &&
      a.attribute_level === "PRODUCT_VARIANT",
  );
  const productAttrs = attrs.filter(
    (a) =>
      a.code_name !== "product_ranking" &&
      a.attribute_level === "PRODUCT",
  );

  // Template schema (form-fields) is the source of which PRODUCT-level
  // fields exist. Product GET only returns stored EAV rows, so a field
  // added to the template after this product was created is missing from
  // ``attrs``. Merge those empty schema fields in so the card matches the
  // template. On a variant they stay read-only (they belong to the parent).
  const productCodeNames = new Set(productAttrs.map((a) => a.code_name));
  const missingTemplateAttrs = (fieldMeta ?? [])
    .filter(
      (f) =>
        f.attribute_level === "PRODUCT" &&
        f.code_name !== "product_ranking" &&
        !productCodeNames.has(f.code_name),
    )
    .sort((a, b) => a.order - b.order);

  // Same merge for variant-level fields. A field added under Variant on
  // the template after this row was created has no EAV value yet.
  const variantCodeNames = new Set(variantAttrs.map((a) => a.code_name));
  const missingVariantAttrs = (variantFieldMeta ?? [])
    .filter(
      (f) =>
        f.code_name !== "product_ranking" &&
        !variantCodeNames.has(f.code_name),
    )
    .sort((a, b) => a.order - b.order);

  /**
   * Build CardFieldItems from attribute values using field metadata.
   *
   * When the brands feature flag is on (and we're not in a variant
   * view), the legacy free-text "brand" EAV attribute is swapped
   * in-place for a SELECT bound to Product.brand_id - preserving the
   * attribute's natural order in the dialog while routing the value
   * to the FK instead of the EAV table on save. The sentinel
   * code_name "__brand_fk__" lets saveAttrs at the page level split
   * the value out into ``brand_uuid``.
   */
  const swapBrand = !!brandsEnabled && !parentProductUuid;
  const toCardItems = (list: AttributeValue[], readOnly = false): CardFieldItem[] =>
    list.map((attr) => {
      if (swapBrand && attr.code_name === "brand") {
        const brandMeta = fieldTypeMap.get(attr.code_name);
        return {
          field: defineField({
            code_name: "__brand_fk__",
            display_name: attr.display_name,
            field_type: "SELECT",
            order: brandMeta?.order ?? attr.order,
            options: [
              { value: "", label: "None" },
              ...(availableBrands ?? []).map((b) => ({
                value: b.uuid,
                label: b.name,
              })),
            ],
          }),
          value: currentBrandUuid ?? "",
          readOnly,
        };
      }
      // The attribute value already carries its own field_type from the
      // product detail response, so prefer it. fieldTypeMap is built from
      // the PRODUCT-level form metadata endpoint, which omits
      // PRODUCT_VARIANT-level attributes (e.g. the MONEY price attrs) -
      // relying on it alone would default those to TEXT and render the
      // { amount, currency } dict as "[object Object]" in the edit form.
      // fieldTypeMap still enriches with options/order when present.
      const meta = fieldTypeMap.get(attr.code_name);
      const resolvedType =
        (attr.field_type as FieldType) ??
        (meta?.field_type as FieldType) ??
        "TEXT";
      return {
        field: defineField({
          code_name: attr.code_name,
          display_name: attr.display_name,
          field_type: resolvedType,
          options: meta?.options,
          order: meta?.order ?? attr.order,
          validation_rules: meta?.validation_rules,
          configuration_data: meta?.configuration_data,
          // File-type product attributes (e.g. the manual PDF) upload under
          // the PRODUCT_DOCUMENT use case; the dialog routes the upload by it.
          file_use_case: FILE_FIELD_TYPES.has(resolvedType)
            ? "PRODUCT_DOCUMENT"
            : undefined,
        }),
        value: attr.value,
        readOnly,
      };
    });

  // Product-level items.
  //
  // Editable on a standalone product (the attrs belong to this product).
  // Read-only on a variant view, since variants inherit product-level
  // attrs from their parent - editing them here would silently mutate
  // the parent.
  const isVariantView = !!parentProductUuid;

  // Template `order` is the sequence the admin set on the template page.
  // Stored EAV rows arrive in write order, so sort both cards after merge.
  const byTemplateOrder = (a: CardFieldItem, b: CardFieldItem) =>
    (a.field.order ?? Number.POSITIVE_INFINITY) -
    (b.field.order ?? Number.POSITIVE_INFINITY);

  const productItems: CardFieldItem[] = [
    ...toCardItems(productAttrs, isVariantView),
    ...missingTemplateAttrs.map((attr) => {
      const resolvedType = (attr.field_type as FieldType) ?? "TEXT";
      return {
        field: defineField({
          code_name: attr.code_name,
          display_name: attr.display_name,
          field_type: resolvedType,
          options: attr.options,
          order: attr.order,
          validation_rules: attr.validation_rules,
          configuration_data: attr.configuration_data,
          file_use_case: FILE_FIELD_TYPES.has(resolvedType)
            ? "PRODUCT_DOCUMENT"
            : undefined,
        }),
        value: productData?.[attr.code_name],
        readOnly: isVariantView,
      };
    }),
  ].sort(byTemplateOrder);

  const variantItems: CardFieldItem[] = [
    ...toCardItems(variantAttrs),
    ...missingVariantAttrs.map((attr) => {
      const resolvedType = (attr.field_type as FieldType) ?? "TEXT";
      return {
        field: defineField({
          code_name: attr.code_name,
          display_name: attr.display_name,
          field_type: resolvedType,
          options: attr.options,
          order: attr.order,
          validation_rules: attr.validation_rules,
          configuration_data: attr.configuration_data,
          file_use_case: FILE_FIELD_TYPES.has(resolvedType)
            ? "PRODUCT_DOCUMENT"
            : undefined,
        }),
        value: productData?.[attr.code_name],
      };
    }),
  ].sort(byTemplateOrder);

  const gridCols = "sm:grid-cols-2 xl:grid-cols-4";
  const editConfig = {
    onSave: saveAttrs,
    isPending: savingAttrs,
    canEdit: true,
    onFileUpload: handleFileUpload,
  };

  const sections: EditableGridSection[] = [];

  // Product Line Attributes first (top) - read-only
  if (productItems.length > 0) {
    sections.push({
      title: "Product Line Attributes",
      icon: <Package className="h-3.5 w-3.5" />,
      items: productItems,
      gridClassName: gridCols,
    });
  }

  // Variant-level attributes second (bottom, editable)
  if (variantItems.length > 0) {
    sections.push({
      title: "Attributes",
      icon: <FileText className="h-3.5 w-3.5" />,
      items: variantItems,
      editConfig,
      className: sections.length > 0 ? "bg-[var(--bg-subtle)]" : undefined,
      gridClassName: gridCols,
    });
  }

  // Fallback: if all attrs were one level, show everything in one section
  if (sections.length === 0) {
    sections.push({
      title: "Attributes",
      icon: <FileText className="h-3.5 w-3.5" />,
      items: toCardItems(attrs.filter((a) => a.code_name !== "product_ranking")),
      editConfig,
      gridClassName: gridCols,
    });
  }

  // If only product-level section exists (no variant attrs), attach edit config
  if (sections.length === 1 && !sections[0].editConfig) {
    sections[0].editConfig = editConfig;
  }

  return <EditableDetailGridCard sections={sections} className={className} />;
}

// ---------------------------------------------------------------------------
// Variants Content (inside DetailCard)
// ---------------------------------------------------------------------------

function VariantsCard({ productUuid }: { productUuid: string }) {
  const [addOpen, setAddOpen] = useState(false);
  return (
    <>
      <DetailCard
        title="Variants"
        icon={<Package className="h-3.5 w-3.5" />}
        collapsible={false}
        trailing={
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => setAddOpen(true)}
            aria-label="Add variant"
            className="cursor-pointer text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        }
      >
        <VariantsContent productUuid={productUuid} />
      </DetailCard>
      <AddVariantDialog
        productUuid={productUuid}
        open={addOpen}
        onOpenChange={setAddOpen}
      />
    </>
  );
}

function VariantsContent({ productUuid }: { productUuid: string }) {
  const navigate = useNavigate();
  const { data: variants, isLoading } = useProductVariants(productUuid);

  if (isLoading)
    return (
      <div className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
        Loading...
      </div>
    );
  if (!variants?.length)
    return (
      <div className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
        No variants.
      </div>
    );

  return (
    <FilterableDetailList
      items={variants}
      getItemKey={(v) => v.uuid}
      // Status is included so users can quickly narrow on e.g. "draft".
      getSearchText={(v) =>
        [v.cached_short_name, v.status].filter(Boolean).join(" ")
      }
      getItemOnClick={(v) => () => navigate(`/manage/products/${v.uuid}`)}
      renderItem={(v) => (
        <div className="flex w-full min-w-0 items-center justify-between gap-3">
          <span className="min-w-0 flex-1 truncate text-[13px] leading-none text-[var(--green)]">
            {v.cached_short_name || "Untitled variant"}
          </span>
          <span className="shrink-0 text-[11px] text-[var(--ink-3)]">
            {v.status.toLowerCase()}
          </span>
        </div>
      )}
      scrollable
      maxHeight="max-h-[360px]"
      itemLabel="variants"
      searchPlaceholder="Search variants"
      emptyFilteredState="No variants match this filter."
      className="bg-[var(--bg)]"
      headerClassName="border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-3 mb-0"
    />
  );
}

// ---------------------------------------------------------------------------
// Add Variant Dialog
// ---------------------------------------------------------------------------

const ADD_VARIANT_FORM_NAME = "add-variant-form";

function AddVariantDialog({
  productUuid,
  open,
  onOpenChange,
}: {
  productUuid: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { data: variantFields, isLoading: fieldsLoading } =
    useVariantFormMetadata(open ? productUuid : null);
  const createVariant = useCreateVariant();

  // Tracks which footer button was pressed (Save Draft vs Publish) so the
  // shared submit handler knows which status to send. Set on click *before*
  // the form's submit event fires.
  const intendedStatusRef = useRef<"DRAFT" | "PUBLISHED">("DRAFT");

  const sortedFields = useMemo(
    () => (variantFields ?? []).slice().sort((a, b) => a.order - b.order),
    [variantFields],
  );

  const generatedFields = useMemo<FormField[]>(
    () =>
      sortedFields.map((field) =>
        // Validation mode is recomputed per submit attempt below - we pass
        // 'submit' here so non-required fields aren't gated until the user
        // actually tries to publish.
        buildFormFieldFromMeta(field, undefined, "submit"),
      ),
    [sortedFields],
  );

  const formConfig = useMemo(
    () => ({
      name: ADD_VARIANT_FORM_NAME,
      submit: "footer" as const,
      fields: generatedFields,
    }),
    [generatedFields],
  );

  const { Form, ref } = useForm(
    formConfig,
    async (formValues) => {
      const status = intendedStatusRef.current;
      try {
        const variant = await createVariant.mutateAsync({
          productUuid,
          attribute_values: formValues,
          status,
        });
        toast.success(
          status === "PUBLISHED" ? "Variant published" : "Variant saved as draft",
        );
        onOpenChange(false);
        if (variant?.uuid) {
          navigate(`/manage/products/${variant.uuid}`);
        }
      } catch (err) {
        toastApiError(err, "Failed to create variant");
      }
    },
    [open, generatedFields, productUuid],
  );

  // Reset to a fresh form whenever the dialog opens. Using the open→true
  // transition (rather than a setTimeout on close) keeps reset behaviour
  // independent of the dialog's animation duration.
  useEffect(() => {
    if (open) {
      ref.current?.resetForm();
      intendedStatusRef.current = "DRAFT";
    }
  }, [open, ref]);

  function submitWithStatus(status: "DRAFT" | "PUBLISHED") {
    intendedStatusRef.current = status;
    ref.current?.submitForm();
  }

  const isPending = createVariant.isPending;
  const showForm = !fieldsLoading && sortedFields.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[520px]" showCloseButton={false} bare>
        <DialogHeader className="border-b-[0.5px] border-b-[var(--rule)] px-5 py-4">
          <DialogTitle className="text-xs font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Add variant
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="px-5 py-4">
          {fieldsLoading ? (
            <p className="py-4 text-center text-[13px] text-[var(--ink-3)]">
              Loading form...
            </p>
          ) : sortedFields.length === 0 ? (
            <p className="py-4 text-center text-[13px] text-[var(--ink-3)]">
              No variant attributes are defined on this product&apos;s template.
            </p>
          ) : (
            <Form className="flex flex-col gap-4" />
          )}
        </DialogBody>

        <DialogFooter className="flex items-center justify-end gap-2 border-t-[0.5px] border-t-[var(--rule)] px-5 py-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => submitWithStatus("DRAFT")}
            disabled={isPending || !showForm}
            className="cursor-pointer"
          >
            Save draft
          </Button>
          <Button
            type="button"
            onClick={() => submitWithStatus("PUBLISHED")}
            disabled={isPending || !showForm}
            className="cursor-pointer"
          >
            Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Registrations Content (inside DetailCard)
// ---------------------------------------------------------------------------

function RegistrationsContent({ productUuid }: { productUuid: string }) {
  const navigate = useNavigate();
  const { locale } = useFormatting();
  const { page, setPage, pageSize, setPageSize } = usePagination(5);

  const params = useMemo(
    () => ({
      product_uuid: productUuid,
      page: String(page),
      page_size: String(pageSize),
    }),
    [productUuid, page, pageSize],
  );

  const { data, isLoading } = useHoldings(params);
  const holdings = data?.results ?? [];
  const totalCount = data?.count ?? 0;

  if (isLoading)
    return (
      <div className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
        Loading...
      </div>
    );
  if (holdings.length === 0)
    return (
      <div className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
        Registrations will appear here
      </div>
    );

  return (
    <>
      {holdings.map((h) => (
        <FieldRow
          key={h.uuid}
          label={
            h.consumer_account
              ? `${h.consumer_account.first_name} ${h.consumer_account.last_name}`.trim() ||
                h.consumer_account.email
              : "-"
          }
          value={new Date(h.created_at).toLocaleDateString(locale, {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
          link
          onLinkClick={() => navigate(`/registrations/${h.uuid}`)}
        />
      ))}
      <div className="px-4 py-2">
        <PaginationControls
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Availability Card - time-based geographic availability
// ---------------------------------------------------------------------------

function formatAvailDate(d: string | null, locale: string): string {
  if (!d) return "Present";
  return new Date(d + "T00:00:00").toLocaleDateString(locale, {
    month: "short",
    year: "numeric",
  });
}

/** Compute the broadest time window across all availability records. */
function broadestWindow(
  records: ProductAvailabilityRecord[],
): { start: string; end: string | null } | null {
  if (!records.length) return null;
  let earliest = records[0].effective_start;
  let latest: string | null = records[0].effective_end;
  let hasOpenEnd = latest === null;

  for (const r of records) {
    if (r.effective_start < earliest) earliest = r.effective_start;
    if (r.effective_end === null) {
      hasOpenEnd = true;
    } else if (!hasOpenEnd && (latest === null || r.effective_end > latest)) {
      latest = r.effective_end;
    }
  }
  return { start: earliest, end: hasOpenEnd ? null : latest };
}

/** Group availability records by time window (effective_start + effective_end). */
function groupByTimeWindow(records: ProductAvailabilityRecord[]) {
  const groups = new Map<
    string,
    { start: string; end: string | null; records: ProductAvailabilityRecord[] }
  >();
  for (const r of records) {
    const key = `${r.effective_start}|${r.effective_end ?? ""}`;
    if (!groups.has(key)) {
      groups.set(key, {
        start: r.effective_start,
        end: r.effective_end,
        records: [],
      });
    }
    groups.get(key)!.records.push(r);
  }
  // Sort: open-ended (current) first, then by start desc
  return [...groups.values()].sort((a, b) => {
    if (a.end === null && b.end !== null) return -1;
    if (a.end !== null && b.end === null) return 1;
    return b.start.localeCompare(a.start);
  });
}

/** Filter to records that are currently active (start <= today, end null or >= today). */
function currentRecords(
  records: ProductAvailabilityRecord[],
): ProductAvailabilityRecord[] {
  const today = new Date().toISOString().slice(0, 10);
  return records.filter(
    (r) =>
      r.effective_start <= today &&
      (r.effective_end === null || r.effective_end >= today),
  );
}

function AvailabilityCard({
  availability,
  allPsaZones,
  editingAvail,
  setEditingAvail,
  productUuid,
  addAvail,
  removeAvail,
  updateAvail,
}: {
  availability: ProductAvailabilityRecord[];
  allPsaZones: GeoZone[];
  editingAvail: boolean;
  setEditingAvail: (v: boolean) => void;
  productUuid: string;
  addAvail: ReturnType<typeof useAddProductAvailability>;
  removeAvail: ReturnType<typeof useRemoveProductAvailability>;
  updateAvail: ReturnType<typeof useUpdateProductAvailability>;
}) {
  const { t } = useTranslation();
  const { locale } = useFormatting();
  const [addingWindow, setAddingWindow] = useState(false);
  const [confirmRemoveAvail, setConfirmRemoveAvail] =
    useState<ProductAvailabilityRecord | null>(null);
  const [editingRecord, setEditingRecord] =
    useState<ProductAvailabilityRecord | null>(null);

  const window = broadestWindow(availability);
  const current = currentRecords(availability);
  const grouped = groupByTimeWindow(availability);

  const uniqueCurrentZones = useMemo(
    () =>
      [...new Map(current.map((r) => [r.zone_uuid, r])).values()].sort((a, b) =>
        a.zone_name.localeCompare(b.zone_name),
      ),
    [current],
  );
  const availabilityFields = useMemo<FormField[]>(
    () => [
      {
        type: 'datepicker',
        name: 'effective_start',
        label: 'Start date',
        value: '',
        required: 'Start date is required',
      },
      {
        type: 'datepicker',
        name: 'effective_end',
        label: 'End date (blank = ongoing)',
        value: '',
      },
      {
        type: 'multiSelect',
        name: 'zone_uuids',
        label: `Select zones`,
        value: [],
        className: 'col-span-2',
        required: 'Select at least one zone',
        options: [...allPsaZones]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((zone) => ({
            value: zone.uuid,
            label: `${zone.name} ${zone.code ? `(${zone.code})` : ''}`.trim(),
          })),
      },
    ],
    [allPsaZones],
  )
  const { Form: AvailabilityWindowForm, ref: availabilityFormRef } = useForm(
    {
      name: `availability-window-form-${productUuid}`,
      submit: 'footer',
      fields: availabilityFields,
    },
    async (rawValues) => {
      const values = rawValues as {
        effective_start: string
        effective_end?: string
        zone_uuids: string[]
      }
      await Promise.all(
        (values.zone_uuids || []).map((zoneUuid) =>
          addAvail.mutateAsync({
            productUuid,
            data: {
              zone_uuid: zoneUuid,
              effective_start: values.effective_start,
              effective_end: values.effective_end || undefined,
            },
          }),
        ),
      )
      setAddingWindow(false)
    },
    [addingWindow, allPsaZones.length, productUuid],
  )

  const editFields = useMemo<FormField[]>(
    () => [
      {
        type: 'datepicker',
        name: 'effective_start',
        label: t('productDetail.startDate', { defaultValue: 'Start date' }),
        value: editingRecord?.effective_start ?? '',
        required: t('productDetail.startDateRequired', {
          defaultValue: 'Start date is required',
        }),
      },
      {
        type: 'datepicker',
        name: 'effective_end',
        label: t('productDetail.endDateOngoing', {
          defaultValue: 'End date (blank = ongoing)',
        }),
        value: editingRecord?.effective_end ?? '',
      },
      {
        type: 'select',
        name: 'zone_uuid',
        label: t('productDetail.zone', { defaultValue: 'Zone' }),
        value: editingRecord?.zone_uuid ?? '',
        className: 'col-span-2',
        required: t('productDetail.zoneRequired', { defaultValue: 'Zone is required' }),
        options: [...allPsaZones]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((zone) => ({
            value: zone.uuid,
            label: `${zone.name} ${zone.code ? `(${zone.code})` : ''}`.trim(),
          })),
      },
    ],
    [allPsaZones, editingRecord, t],
  )
  const { Form: AvailabilityEditForm, ref: editFormRef } = useForm(
    {
      name: `availability-edit-form-${productUuid}`,
      submit: 'footer',
      fields: editFields,
    },
    async (rawValues) => {
      if (!editingRecord) return
      const values = rawValues as {
        effective_start: string
        effective_end?: string
        zone_uuid: string
      }
      await updateAvail.mutateAsync({
        productUuid,
        availUuid: editingRecord.uuid,
        data: {
          zone_uuid: values.zone_uuid,
          effective_start: values.effective_start,
          effective_end: values.effective_end || undefined,
        },
      })
      setEditingRecord(null)
    },
    [editingRecord?.uuid, allPsaZones.length, productUuid],
  )

  // Hard-reset the edit form to the record being edited whenever it opens or
  // switches, so it never shows a previous record's values.
  useEffect(() => {
    if (!editingRecord) return
    editFormRef.current?.resetForm({
      values: {
        effective_start: editingRecord.effective_start,
        effective_end: editingRecord.effective_end ?? '',
        zone_uuid: editingRecord.zone_uuid,
      },
    })
  }, [editingRecord, editFormRef])

  return (
    <DetailCard
      title={`Plant Service Areas (${availability.length})`}
      icon={<MapPin className="h-3.5 w-3.5" />}
      collapsible={false}
      trailing={
        <Button
          variant="ghost"
          size="xs"
          onClick={() => setEditingAvail(!editingAvail)}
          className="h-auto gap-1 px-0 py-0 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)] hover:text-[var(--green)]"
        >
          {editingAvail ? (
            <>
              <X className="h-3 w-3" /> Done
            </>
          ) : (
            <>
              <Pencil className="h-3 w-3" /> Edit
            </>
          )}
        </Button>
      }
    >
      {editingAvail ? (
        /* ── Edit mode: grouped by time window ── */
        <div className="space-y-3 px-4 pb-2 pt-1">
          {grouped.map((group) => (
            <div key={`${group.start}|${group.end ?? ""}`}>
              <div className="mb-1.5 flex items-center gap-1.5">
                <Calendar className="h-3 w-3 text-[var(--ink-3)]" />
                <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                  {formatAvailDate(group.start, locale)} - {formatAvailDate(group.end, locale)}
                </span>
                {group.end === null && (
                  <Badge variant="outline" className="px-1.5 py-0 text-[9px]">
                    Current
                  </Badge>
                )}
              </div>
              <div className="rounded-[4px] border-[0.5px] border-[var(--rule)]">
                {group.records
                  .sort((a, b) => a.zone_name.localeCompare(b.zone_name))
                  .map((r) => (
                    <div
                      key={r.uuid}
                      className="flex items-center justify-between border-b-[0.5px] border-b-[var(--rule)] px-3 py-1.5 last:border-b-0"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-[12px]">{r.zone_name}</span>
                        <span className="text-[10px] text-[var(--ink-3)]">
                          {r.zone_code}
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => setEditingRecord(r)}
                          className="h-5 w-5 text-[var(--ink-3)] hover:text-[var(--green)]"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => setConfirmRemoveAvail(r)}
                          className="h-5 w-5 text-[var(--ink-3)] hover:text-[var(--status-error)]"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
          {availability.length === 0 && !addingWindow && (
            <p className="mb-3 text-[12px] text-[var(--ink-3)]">
              No availability windows configured.
            </p>
          )}
          {editingRecord && (
            <div className="mt-3 rounded-[4px] border-[0.5px] border-[var(--green)] bg-[var(--green-bg)] p-3">
              <p className="mb-2 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-2)]">
                {t("productDetail.editAvailability", {
                  defaultValue: "Edit availability",
                })}
              </p>
              <AvailabilityEditForm className="mb-3 grid grid-cols-2 gap-3" />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="xs" onClick={() => setEditingRecord(null)}>
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </Button>
                <Button
                  size="xs"
                  type="submit"
                  form={`availability-edit-form-${productUuid}`}
                  disabled={updateAvail.isPending}
                >
                  {updateAvail.isPending
                    ? t("common.saving", { defaultValue: "Saving..." })
                    : t("common.save", { defaultValue: "Save" })}
                </Button>
              </div>
            </div>
          )}
          <ConfirmationDialog
            open={confirmRemoveAvail !== null}
            onOpenChange={(open) => {
              if (!open) setConfirmRemoveAvail(null);
            }}
            title="Remove availability?"
            description={
              confirmRemoveAvail
                ? `${confirmRemoveAvail.zone_name} will no longer be an availability zone for this product.`
                : undefined
            }
            confirmLabel="Remove"
            destructive
            isPending={removeAvail.isPending}
            onConfirm={() => {
              if (!confirmRemoveAvail) return;
              removeAvail.mutate(
                { productUuid, availUuid: confirmRemoveAvail.uuid },
                { onSuccess: () => setConfirmRemoveAvail(null) },
              );
            }}
          />
          {/* Add window form */}
          {addingWindow ? (
            <div className="mt-3 rounded-[4px] border-[0.5px] border-[var(--green)] bg-[var(--green-bg)] p-3">
              <p className="mb-2 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-2)]">
                New availability window
              </p>
              <AvailabilityWindowForm className="mb-3 grid grid-cols-2 gap-3" />
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    availabilityFormRef.current?.resetForm()
                    setAddingWindow(false)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  type="submit"
                  form={`availability-window-form-${productUuid}`}
                  disabled={addAvail.isPending}
                >
                  {addAvail.isPending ? "Saving..." : "Add Window"}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="xs"
              onClick={() => setAddingWindow(true)}
              className="mt-2 gap-1"
            >
              <Plus className="h-3 w-3" />
              Add availability window
            </Button>
          )}
        </div>
      ) : !availability.length ? (
        <div className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
          No availability zones assigned.
        </div>
      ) : (
        /* ── Read mode: broadest window summary + current zone badges ── */
        <div className="px-4 pb-3 pt-1">
          {/* Broadest time window summary */}
          {window && (
            <div className="mb-2.5 flex items-center gap-2 text-[11px] text-[var(--ink-3)]">
              <Calendar className="h-3 w-3" />
              <span>
                Available {formatAvailDate(window.start, locale)} -{" "}
                {formatAvailDate(window.end, locale)}
              </span>
            </div>
          )}
          {/* Current zone badges */}
          {uniqueCurrentZones.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {uniqueCurrentZones.map((a) => (
                <Badge key={a.uuid} variant="secondary" className="text-[11px]">
                  {a.zone_name}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-[12px] text-[var(--ink-3)]">
              No zones currently active. {availability.length} historical record
              {availability.length !== 1 ? "s" : ""}.
            </p>
          )}
        </div>
      )}
    </DetailCard>
  );
}
